import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button from '@components/Button';
import Form from '@components/Form';
import IconMdi from '@components/IconMdi';
import InputText from '@components/InputText';
import { Show } from '@components/Show';
import { useForm } from '@hooks/form';
import { mdiChevronLeft } from '@mdi/js';
import { signIn } from 'aws-amplify/auth';
import { get } from 'lodash-es';
import * as yup from 'yup';

import Nav from '../../../routes';
import { isPasswordExpired, PasswordExpiredBanner } from '../components/PasswordExpiredBanner';

import { INCORRECT_CREDENTIALS_MESSAGE, isThrottledError, SIGN_IN_THROTTLED_MESSAGE } from './errorMessages';

interface Props {
  email: string;
  onBack: () => void;
}

const validationSchema = yup.object().shape({
  password: yup.string().required(),
});

const PasswordStep = ({ email, onBack }: Props) => {
  const navigate = useNavigate();
  const location = useLocation();

  const { getFieldProps, canSubmit, onSubmit, formError, isLoading } = useForm<{ password: string }>({
    initialState: { password: '' },
    validationSchema,
    onSubmit: async ({ password }) => {
      const result = await signIn({ username: email, password });
      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        return navigate(Nav.SetupPassword, { state: { email, password } });
      }
      const pathName = get(location, 'state.from.pathname', '').trim();
      navigate(pathName ? pathName : Nav.Submissions);
    },
  });

  const handleSubmit = async () => {
    await onSubmit();
  };

  const isPasswordExpiredError = isPasswordExpired(formError);
  const isThrottled = isThrottledError(formError);
  // Deliberately the same message whether the password is wrong or no account exists for this
  // email — showing anything more specific would leak account existence (see CB-3058 spec).
  const credentialsErrorMessage =
    formError && !isPasswordExpiredError && !isThrottled
      ? INCORRECT_CREDENTIALS_MESSAGE
      : isThrottled
        ? SIGN_IN_THROTTLED_MESSAGE
        : undefined;

  return (
    <>
      <Show when={isPasswordExpiredError}>
        <PasswordExpiredBanner />
      </Show>
      <Form onSubmit={handleSubmit}>
        <InputText className="mb-6" name="email" value={email} errors={credentialsErrorMessage} isPublic isDisabled />
        <InputText
          className="mb-6"
          labelText="Enter your password"
          errors={credentialsErrorMessage}
          isPublic
          autoComplete="current-password"
          link={
            <Link className="cb--link text-primary-500 font-medium" to={Nav.ForgotPassword} state={{ email }}>
              Forgot password?
            </Link>
          }
          type="password"
          {...getFieldProps('password')}
        />
        <Button kind="primary" type="submit" isDisabled={!canSubmit} loading={isLoading} className="h-10 w-full">
          Sign in
        </Button>
        <button
          type="button"
          className="cb--link text-info-500 mt-3 flex w-fit items-center gap-1 text-sm font-medium"
          onClick={onBack}
        >
          <IconMdi path={mdiChevronLeft} size={0.7} />
          Back
        </button>
      </Form>
    </>
  );
};

export default PasswordStep;
